<script setup lang="ts">
import type { Component, DeepReadonly } from "vue";
import { computed, ref, useTemplateRef } from "vue";

import type { SightlineCell, SightlineTableBlock } from "@/backend/natsstubs/sightline/models";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useColumnResize } from "@/composables/useColumnResize";
import type { Icon } from "@/model/icon";
import { boardHref, stickyIdHref } from "@/router/navigation";
import { useBoardsStore } from "@/store/boards";

import SightlineResultHeader from "../SightlineResultHeader.vue";
import SightlineArtCell from "./SightlineArtCell.vue";
import SightlineBoardCell from "./SightlineBoardCell.vue";
import SightlineNumberCell from "./SightlineNumberCell.vue";
import SightlineObjectiveCell from "./SightlineObjectiveCell.vue";
import SightlineStickyCell from "./SightlineStickyCell.vue";
import SightlineTeamCell from "./SightlineTeamCell.vue";
import SightlineTextCell from "./SightlineTextCell.vue";
import SightlineUserCell from "./SightlineUserCell.vue";

const props = defineProps<{
  block: DeepReadonly<SightlineTableBlock>;
  fullView?: boolean;
  triggerId?: string;
}>();
const emit = defineEmits<{ open: [] }>();

const CAP = 10;

const boardsStore = useBoardsStore();

const expanded = ref(false);
const sort = ref<{ columnIndex: number; direction: "ascending" | "descending" } | null>(null);

const sorted = computed(() => {
  const current = sort.value;
  if (!current) return props.block.rows;
  const sign = current.direction === "ascending" ? 1 : -1;
  return [...props.block.rows].sort(
    (a, b) => sign * compare(sortKey(a[current.columnIndex]), sortKey(b[current.columnIndex])),
  );
});
const visible = computed(() =>
  props.fullView || expanded.value ? sorted.value : sorted.value.slice(0, CAP),
);
const hiddenCount = computed(() => props.block.rows.length - visible.value.length);

const sortIcon = computed<Icon>(() =>
  sort.value?.direction === "descending" ? "arrow/chevron-down" : "arrow/chevron-up",
);

function toggleSort(columnIndex: number) {
  const ascending = sort.value?.columnIndex === columnIndex && sort.value.direction === "ascending";
  sort.value = { columnIndex, direction: ascending ? "descending" : "ascending" };
}

function sortKey(cell: DeepReadonly<SightlineCell>) {
  if (cell.kind === "number") return cell.value;
  if (cell.kind === "text") return cell.text;
  return cell.label;
}

function compare(a: string | number, b: string | number) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

// columns are homogeneous, so the first row tells which ones are numeric
const numericColumns = computed(
  () => props.block.rows[0]?.map((cell) => cell.kind === "number") ?? [],
);

const headerRowRef = useTemplateRef<HTMLTableRowElement>("headerRow");
const { widths, startResize } = useColumnResize({
  // resizing exists only for headed tables, so the header row is present
  measure: () => [...headerRowRef.value!.cells].map((cell) => cell.getBoundingClientRect().width),
});

function columnStyle(columnIndex: number) {
  if (widths.value) return { width: `${widths.value[columnIndex]}px` };
  if (numericColumns.value[columnIndex]) return { width: "120px" };
  return undefined;
}

const content: Record<SightlineCell["kind"], Component> = {
  sticky: SightlineStickyCell,
  objective: SightlineObjectiveCell,
  team: SightlineTeamCell,
  user: SightlineUserCell,
  board: SightlineBoardCell,
  art: SightlineArtCell,
  text: SightlineTextCell,
  number: SightlineNumberCell,
};

const hrefs = computed(() => visible.value.map((row) => row.map(cellHref)));

function cellHref(cell: DeepReadonly<SightlineCell>) {
  if (cell.kind === "sticky" && cell.boardId in boardsStore.boards) {
    return stickyIdHref(cell.id, boardsStore.boards[cell.boardId], "enlarge");
  }
  if (cell.kind === "board" && cell.id in boardsStore.boards) {
    return boardHref(boardsStore.boards[cell.id]);
  }
}
</script>

<template>
  <div class="sightline-table-block">
    <SightlineResultHeader
      v-if="!fullView"
      :title="$t('sightline.table')"
      :trigger-id="triggerId"
      @open="emit('open')"
    />
    <div class="table-box">
      <table>
        <colgroup>
          <col
            v-for="(_, columnIndex) in block.columns"
            :key="columnIndex"
            :style="columnStyle(columnIndex)"
          />
        </colgroup>
        <thead v-if="block.columns.length > 0">
          <tr ref="headerRow">
            <th
              v-for="(column, columnIndex) in block.columns"
              :key="columnIndex"
              scope="col"
              :class="{ numeric: numericColumns[columnIndex] }"
              :aria-sort="sort?.columnIndex === columnIndex ? sort.direction : 'none'"
            >
              <button
                type="button"
                class="column-header"
                :title="column"
                @click="toggleSort(columnIndex)"
              >
                <span class="label">{{ column }}</span>
                <SvgIcon
                  v-if="sort?.columnIndex === columnIndex"
                  :name="sortIcon"
                  width="16"
                  height="16"
                  class="sort-caret"
                />
              </button>
              <span
                v-if="columnIndex < block.columns.length - 1"
                class="resize-handle"
                aria-hidden="true"
                @pointerdown.prevent="startResize(columnIndex, $event)"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in visible" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :class="{ numeric: numericColumns[cellIndex] }"
            >
              <component
                :is="hrefs[rowIndex][cellIndex] ? 'a' : 'div'"
                :href="hrefs[rowIndex][cellIndex]"
                :target="hrefs[rowIndex][cellIndex] && fullView ? '_blank' : undefined"
                class="cell"
              >
                <component :is="content[cell.kind]" :cell />
              </component>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="!fullView && (hiddenCount > 0 || expanded)" class="toggle">
      <BaseButton variant="ghost" size="small" @click="expanded = !expanded">
        {{ expanded ? $t("sightline.showLess") : $t("sightline.showAll", { count: hiddenCount }) }}
      </BaseButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/utils";

.sightline-table-block {
  width: 100%;

  &:hover,
  &:focus-within {
    --sightline-expand-opacity: 1;
  }

  .table-box {
    border: 1px solid colors.$border-light;
    border-radius: 8px;
    overflow: hidden;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th {
    @include typography.small("bold");

    position: relative;
    height: 32px;
    text-align: left;
    vertical-align: middle;
    color: colors.$text-subtle;
    background: colors.$background-ui;
    border-bottom: 1px solid colors.$border-light;

    &.numeric .column-header {
      justify-content: flex-end;
    }
  }

  .column-header {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 12px;
    cursor: pointer;
    outline: revert;

    &:hover {
      color: colors.$text;
    }

    .label {
      @include utils.ellipsis;
    }

    .sort-caret {
      flex-shrink: 0;
    }
  }

  .resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      right: 3px;
      width: 2px;
      height: 14px;
      transform: translateY(-50%);
      border-radius: 1px;
      background: colors.$border-light;
    }

    &:hover::after {
      background: colors.$text-subtle;
    }
  }

  td {
    height: 40px;
    padding: 0;
    background: colors.$background-ui;

    &.numeric {
      white-space: nowrap;

      .cell {
        justify-content: flex-end;
      }
    }
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid colors.$border-light;
  }

  .cell {
    white-space: nowrap;
    text-align: left;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    color: colors.$text;
    text-decoration: none;

    &[href] {
      cursor: pointer;
      outline: revert;

      &:hover {
        background: colors.$background-grey-light;
      }
    }
  }

  .toggle {
    display: flex;
    justify-content: center;
    margin-top: 4px;
  }
}
</style>
